import React, { useState } from 'react';
import { classes, style } from 'typestyle';
import { colorWhite, rebrand } from '../../../theme/color';
import { spacing } from '../../../theme/spacing';
import { important, px } from 'csx';
import { isEmpty } from '../utilities/objects';
import { formatNonDecimalCurrency } from '../../../lib/currency';
import { Tooltip } from '../atoms/Tooltip';
import { InfoFilled } from '../atoms/icons/InfoFilled';
import { centerCenter, flexRoot, start } from 'csstips';
import { resetButton } from '../../../theme/reset';
import { CloseCross } from '../atoms/icons/CloseCross';
import { Link } from '../atoms/Link';
import { ACCOUNT_PLANS_ROUTE, BASE_LINE_HEIGHT } from '../../../constants';
import { ChannelSchema, RatecardSchema } from '../../../types/schema';
import { isOngoingSubscription, isTrialSubscription } from '../utilities/types';

const styles = {
  wrapper: style({
    color: rebrand.contrast[30].toString(),
    marginRight: spacing.XSMALL,
  }),
  subscriptionStatusText: style({
    fontWeight: 700,
    margin: 0,
    color: colorWhite.toString(),
  }),
  unsubscribedText: style({
    color: rebrand.semantic.warning['50'].toString(),
  }),
  subscribedActiveText: style({
    color: rebrand.secondary['50'].toString(),
  }),
  subscriptionDetails: style({
    fontSize: px(12),
    marginTop: spacing.XXSMALL,
  }),
  tooltip: style({
    backgroundColor: colorWhite.toHexString(),
    color: rebrand.contrast[40].toString(),
    fontSize: px(12),
    lineHeight: BASE_LINE_HEIGHT,
    maxWidth: px(350),
    padding: spacing.DEFAULT,
    paddingRight: spacing.XLARGE, // To account for close button
    $nest: {
      '& strong': {
        color: rebrand.primary[100].toString(),
        fontWeight: 700,
      },
      '& a': {
        color: 'inherit',
        textDecoration: 'underline',
      },
      '&::after': {
        borderTopColor: important(colorWhite.toHexString()),
      },
    },
  }),
  tooltipBtn: style(resetButton, flexRoot, centerCenter, {
    lineHeight: 1,
    position: 'relative',
    top: px(-4),
    width: px(22),
    height: px(22),
  }),
  tooltipIcon: style({
    display: 'inline-block',
  }),
  tooltipInner: style(flexRoot, start, {
    gap: spacing.XSMALL,
  }),
  tooltipInfoIcon: style({
    flexShrink: 0,
  }),
  tooltipCloseBtn: style(resetButton, flexRoot, centerCenter, {
    position: 'absolute',
    display: 'block',
    lineHeight: 1,
    top: 0,
    right: 0,
    width: px(32),
    height: px(32),
    color: rebrand.contrast[50].toHexString(),
  }),
};

interface NewPlansInfoTooltipProps {
  isVisible: boolean;
  onClose: () => void;
  onToggle: () => void;
}

const NewPlansInfoTooltip: React.FC<NewPlansInfoTooltipProps> = ({ isVisible, onClose, onToggle }) => {
  return (
    <Tooltip
      className={styles.tooltip}
      wrapperClassName={styles.tooltipIcon}
      positions={['top', 'bottom']}
      show={isVisible}
      onClickOutside={onClose}
      disableOnMobile={false}
      content={
        <span className={styles.tooltipInner}>
          <InfoFilled size={20} color={rebrand.primary[100].toString()} className={styles.tooltipInfoIcon} />
          <span>
            <strong>Check out our new subscription plans!</strong> They give you more choice and come with Premium track
            credits as standard. Don&apos;t worry, your current plan is unaffected for now, but you could be saving
            money on licenses, so <Link href={ACCOUNT_PLANS_ROUTE}>head over to our new plans</Link> to take a look!
          </span>

          <button className={styles.tooltipCloseBtn} onClick={onClose}>
            <CloseCross color="inherit" size={16} />
          </button>
        </span>
      }
    >
      <button className={styles.tooltipBtn} onClick={onToggle} aria-label="See information about our new plans">
        <InfoFilled size={12} color={rebrand.contrast[30].toString()} />
      </button>
    </Tooltip>
  );
};

interface RatecardDetailsProps {
  channel?: ChannelSchema;
  isSubscribedWithoutChannel?: boolean;
  unsubscribedRatecard?: RatecardSchema;
  isDashboard?: boolean;
  className?: string;
}

export const RatecardDetails: React.FC<RatecardDetailsProps> = ({
  channel,
  isSubscribedWithoutChannel,
  unsubscribedRatecard,
  className,
  isDashboard = false,
}) => {
  const [newPlansPopupActive, setNewPlansPopupActive] = useState(false);

  const hideNewPlansPopup = (): void => {
    setNewPlansPopupActive(false);
  };

  const toggleNewPlansPopup = (): void => {
    setNewPlansPopupActive((val) => !val);
  };

  const isSubscribed = channel?.is_subscribed || false;
  const currentSubscription = channel?.subscription || null;
  const isSubscribedToV2TrialPlan = currentSubscription && isTrialSubscription(channel.subscription.status);
  const isSubscribedToV2PlanAndCancelled = currentSubscription && !isOngoingSubscription(channel.subscription.status);
  const isSubscribedToLegacyPlan = isSubscribed && !currentSubscription;

  const planType = currentSubscription ? `${currentSubscription.plan_name} Plan` : 'Legacy Creator Plan';
  const additionalLicensePrice = channel?.ratecard
    ? formatNonDecimalCurrency(channel.ratecard.value, channel.ratecard.currency)
    : null;
  const planCredits = currentSubscription?.plan_credits || 0;

  const getPlanAndLicenseText = () => {
    // Unsubscribed user or no user
    if (!channel && !isSubscribedWithoutChannel) {
      const unsubscribedPrice = !isEmpty(unsubscribedRatecard)
        ? formatNonDecimalCurrency(
            unsubscribedRatecard.value || unsubscribedRatecard.rate,
            unsubscribedRatecard.currency
          )
        : 'from $32';

      return {
        planText: '0 Premium track credits, no royalty-free',
        licenceText: `Premium licenses ${unsubscribedPrice} per track`,
      };
    }

    if (isSubscribed && additionalLicensePrice) {
      return {
        planText: `${planCredits} Premium track credit${planCredits !== 1 ? 's' : ''}, unlimited royalty-free`,
        licenceText: `${planCredits > 0 ? 'Additional ' : ''}Premium licenses ${additionalLicensePrice} per track`,
      };
    }

    if (channel && !channel.is_subscribed && additionalLicensePrice) {
      return {
        planText: '0 Premium track credits, no royalty-free',
        licenceText: `Premium licenses ${additionalLicensePrice} per track`,
      };
    }

    if (isSubscribedWithoutChannel) {
      return {
        planText: '0 Premium track credits, unlimited royalty-free',
        licenceText: 'Premium licenses from $8 per track',
      };
    }

    return { planText: '', licenceText: '' };
  };

  const { planText, licenceText } = getPlanAndLicenseText();

  const getFlagText = () => {
    if (isSubscribedToV2TrialPlan) {
      return (
        <span className={styles.unsubscribedText} data-testid="trial-flag">
          {' '}
          Trial{isSubscribedToV2PlanAndCancelled ? ' ending soon' : ''}
        </span>
      );
    }

    if (currentSubscription && !isSubscribedToV2PlanAndCancelled) {
      return (
        <span className={styles.subscribedActiveText} data-testid="active-flag">
          {' '}
          Active
        </span>
      );
    }

    if (isSubscribedToV2PlanAndCancelled) {
      return (
        <span className={styles.unsubscribedText} data-testid="ending-soon-flag">
          {' '}
          Ending soon
        </span>
      );
    }

    return null;
  };

  return (
    <div data-test-rate-card-detailed-text className={classes(styles.wrapper, className)}>
      <p className={classes(styles.subscriptionStatusText)}>
        {isSubscribed || isSubscribedWithoutChannel ? (
          <span data-test-channel-card-status={planType}>
            {planType}
            {getFlagText()}

            {isSubscribedToLegacyPlan && isDashboard && (
              <NewPlansInfoTooltip
                isVisible={newPlansPopupActive}
                onClose={hideNewPlansPopup}
                onToggle={toggleNewPlansPopup}
              />
            )}
          </span>
        ) : (
          <span data-test-channel-card-status={'Not subscribed'} className={styles.unsubscribedText}>
            Not subscribed
          </span>
        )}
      </p>

      {!!planText && <div className={styles.subscriptionDetails}>{planText}</div>}
      {!!licenceText && !isDashboard && <div className={styles.subscriptionDetails}>{licenceText}</div>}
    </div>
  );
};
